import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  useInviteInfo,
  useJoinByInvite,
} from "@tubo/api-models/hooks/tenant-api.hooks";
import { Button } from "@tubo/ui/components/ui/button";
import { Card, CardContent, CardHeader } from "@tubo/ui/components/ui/card";
import { Mail, Clock, Building2, User, AlertCircle } from "lucide-react";
import { useAuthState, useAuthActions } from "@tubo/store";
import { toast } from "sonner";
import { useUiTranslation } from "@tubo/ui/i18n";

const branding = {
  companyName: "Tubo",
  tagline: "智能化管理平台",
};

export const Route = createFileRoute("/invite/$inviteId")({
  component: InvitePage,
});

function InviteLoadingState() {
  const { t } = useUiTranslation("auxiliaryPages");
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="flex items-center gap-2 text-muted-foreground">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        <span>{t("invite.loading")}</span>
      </div>
    </div>
  );
}

function InviteErrorState() {
  const { t } = useUiTranslation("auxiliaryPages");
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <AlertCircle className="mb-2 h-12 w-12 text-destructive" />
          <h1 className="text-xl font-semibold">{t("invite.invalidTitle")}</h1>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          <p className="text-sm text-muted-foreground">
            {t("invite.invalidDescription")}
          </p>
          <Link to="/login">
            <Button variant="outline">{t("invite.returnToLogin")}</Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}

function InviteExpiredState() {
  const { t } = useUiTranslation("auxiliaryPages");
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <AlertCircle className="mb-2 h-12 w-12 text-icon-secondary" />
          <h1 className="text-xl font-semibold">{t("invite.expiredTitle")}</h1>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          <p className="text-sm text-muted-foreground">
            {t("invite.expiredDescription")}
          </p>
          <Link to="/login">
            <Button variant="outline">{t("invite.returnToLogin")}</Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}

interface InviteContentProps {
  inviteId: string;
  tenantName: string;
  inviterName: string;
  expireAt?: string;
  isAuthenticated: boolean;
  isJoining: boolean;
  onLogin: () => void;
  onRegister: () => void;
  onJoin: () => void;
}

function InviteContent({
  inviteId: _inviteId,
  tenantName,
  inviterName,
  expireAt,
  isAuthenticated,
  isJoining,
  onLogin,
  onRegister,
  onJoin,
}: InviteContentProps) {
  const { t, i18n } = useUiTranslation("auxiliaryPages");
  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <header className="border-b">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <span className="text-sm font-bold">T</span>
            </div>
            <span className="text-lg font-semibold">
              {branding.companyName}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {!isAuthenticated && (
              <>
                <Button variant="ghost" size="sm" onClick={onLogin}>
                  {t("invite.login")}
                </Button>
                <Button size="sm" onClick={onRegister}>
                  {t("invite.register")}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="items-center text-center">
            <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-7 w-7 text-primary" />
            </div>
            <h1 className="text-xl font-semibold">{t("invite.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("invite.inviterLine", { name: inviterName })}</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="rounded-lg border bg-surface p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t("invite.teamName")}</p>
                    <p className="text-sm font-medium">{tenantName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-icon-secondary" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t("invite.inviter")}</p>
                    <p className="text-sm font-medium">{inviterName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-icon-secondary" />
                  <div>
                    <p className="text-xs text-muted-foreground">{t("invite.expiresAt")}</p>
                    <p className="text-sm font-medium">
                      {expireAt
                        ? new Date(expireAt).toLocaleDateString(i18n.language)
                        : t("invite.neverExpires")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {isAuthenticated ? (
                <Button
                  className="w-full"
                  size="lg"
                  onClick={onJoin}
                  disabled={isJoining}
                >
                  {isJoining ? t("invite.joining") : t("invite.join")}
                </Button>
              ) : (
                <>
                  <Button className="w-full" size="lg" onClick={onRegister}>
                    {t("invite.noAccountRegister")}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    size="lg"
                    onClick={onLogin}
                  >
                    {t("invite.existingAccountLogin")}
                  </Button>
                </>
              )}
            </div>

            <p className="text-center text-xs text-muted-foreground">
              {isAuthenticated
                ? t("invite.joinAuthenticatedHint")
                : t("invite.joinUnauthenticatedHint")}
            </p>
          </CardContent>
        </Card>
      </main>

      <footer className="border-t py-4">
        <p className="text-center text-xs text-muted-foreground">
          {t("invite.footer", { year: new Date().getFullYear() })}
        </p>
      </footer>
    </div>
  );
}

function InvitePage() {
  const { t } = useUiTranslation("auxiliaryPages");
  const { inviteId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useInviteInfo({ inviteId });
  const { isAuthenticated } = useAuthState();
  const { setAuth } = useAuthActions();

  const joinMutation = useJoinByInvite({
    mutation: {
      onSuccess: (resp) => {
        if (resp.token?.accessToken) {
          setAuth({ token: resp.token.accessToken });
        }
        toast.success(t("invite.joined"));
        navigate({ to: "/" });
      },
      onError: () => {
        toast.error(t("invite.joinFailed"));
      },
    },
  });

  if (isLoading) {
    return <InviteLoadingState />;
  }

  if (error || !data) {
    return <InviteErrorState />;
  }

  const { tenantName, inviterName, valid, expireAt } = data;

  if (!valid) {
    return <InviteExpiredState />;
  }

  const handleLogin = () => {
    navigate({
      to: "/login",
      search: { redirect: `/invite/${inviteId}` },
    });
  };

  const handleRegister = () => {
    navigate({
      to: "/register",
      search: { redirect: `/invite/${inviteId}` },
    });
  };

  const handleJoin = () => {
    joinMutation.mutate({ data: { inviteId } });
  };

  return (
    <InviteContent
      inviteId={inviteId}
      tenantName={tenantName ?? ""}
      inviterName={inviterName ?? ""}
      expireAt={expireAt}
      isAuthenticated={isAuthenticated}
      isJoining={joinMutation.isPending}
      onLogin={handleLogin}
      onRegister={handleRegister}
      onJoin={handleJoin}
    />
  );
}
